import React, { useState, useEffect, useMemo, useRef } from "react";
import { Link, useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/hooks/useAuth";
import { useContrastText } from "@/hooks/useContrastText";
import { UserRole, type StaticPage } from "@webready/shared/schema";
import { useQuery } from '@tanstack/react-query';
import { api, fetchWithSchema } from '@/lib/api';
import { Skeleton } from '@/components/ui/skeleton';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Menu, User, LogOut, ChevronDown, CalendarCheck, Hotel, Phone } from "lucide-react";
import { useAppSettings, useBrandingSettings } from "@/contexts/AppSettingsContext";
import { useAppConfiguration } from "@/hooks/useAppConfiguration";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { cn, phoneToTelHref, phoneToWhatsAppHref } from "@/lib/utils";
import { isWebReadyTenant, WEBREADY_WHATSAPP_GDPR_MESSAGE } from "@/lib/webreadyLegal";
import {
  appleNavContactPillClass,
  appleNavShellHeroClass,
  appleNavShellSolidClass,
} from "@/lib/customSiteChrome";
import { useTranslation } from "@/hooks/useTranslation";
import { getSchemaScopedQueryKey } from "@/lib/schemaStorage";
import { useNavigationLinks } from "@/hooks/useNavigationLinks";
import { useCustomSiteBackdrop } from "@/hooks/useCustomSiteBackdrop";

// Static logo import removed - logo is now only loaded from app settings

const WhatsAppIcon = ({ className }: { className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="currentColor"
    className={className}
    aria-hidden
  >
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
  </svg>
);

interface NavLinkProps {
  href: string;
  children: React.ReactNode;
  /** Navbar peste hero întunecat (design custom home): linkuri albe */
  heroOverlay?: boolean;
}

const NavLink = ({ href, children, heroOverlay }: NavLinkProps) => {
  const [location] = useLocation();
  const isActive = location === href;

  return (
    <Link
      href={href}
      className={cn(
        "transition-colors min-h-[44px] md:min-h-0 flex items-center px-2 py-2 md:py-0 [touch-action:manipulation]",
        heroOverlay
          ? isActive
            ? "text-white font-semibold"
            : "text-white/85 hover:text-white"
          : isActive
            ? "text-primary font-semibold hover:text-primary"
            : "text-muted-foreground hover:text-primary"
      )}
    >
      {children}
    </Link>
  );
};

interface NavbarProps {
  onLogin: () => void;
  onRegister: () => void;
  onAppointmentChat?: () => void;
}

export default function Navbar({ onLogin, onRegister, onAppointmentChat }: NavbarProps) {
  const { t } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [location, navigate] = useLocation();
  const { user, userData, logout } = useAuth();
  const { settings, isLoading: isLoadingSettings } = useAppSettings();
  const { textColorClass } = useContrastText();
  const { appConfig, isLoading: isLoadingAppConfig } = useAppConfiguration();
  const { contactPhone } = useBrandingSettings();
  const { isFeatureEnabled } = useSubscription();
  
  // Track if navbar animations have been applied (only animate on initial page load, not on route changes)
  const [hasAnimated, setHasAnimated] = useState(() => {
    // Check if animations were already applied in this session
    return sessionStorage.getItem('navbar-animated') === 'true';
  });
  
  // Determine application type
  const appointmentType = appConfig?.appointmentType;
  const isClinicHours = appointmentType === 'clinic_hours';
  const isLodgingDays = appointmentType === 'lodging_days';
  const isWebsitePresentation = appointmentType === 'website_presentation';
  
  // Check if booking is enabled (only for clinic_hours and lodging_days)
  const isBookingEnabled = isFeatureEnabled('enable_booking') && (isClinicHours || isLodgingDays);
  
  // Determine button text and link based on appointment type
  const bookingButtonText = isClinicHours ? t('public.navbar.bookAppointment', 'Programează-te') : t('public.navbar.bookRoom', 'Rezervă o cameră');
  const bookingButtonHref = isClinicHours ? '/appointments' : '/services';
  const bookingButtonIcon = isClinicHours ? CalendarCheck : Hotel;

  // Fetch main static pages (custom: false)
  const { data: mainPages, isLoading: isLoadingMain } = useQuery<StaticPage[]>({
    queryKey: getSchemaScopedQueryKey(['static-pages-main']),
    queryFn: async () => {
      const res = await fetchWithSchema('/api/static-pages/main');
      let text = await res.text();
      try {
        const json = JSON.parse(text);
        if (!res.ok) throw new Error('Failed to fetch main static pages');
        return json;
      } catch (e) {
        console.error('Eroare la parsarea răspunsului /api/static-pages/main:', text);
        throw e;
      }
    },
    staleTime: 2 * 60 * 1000 // 2 minutes - reduced for faster updates in production
  });

  // Fetch custom static pages (custom: true)
  const { data: customPages, isLoading: isLoadingCustom } = useQuery<StaticPage[]>({
    queryKey: getSchemaScopedQueryKey(['static-pages-nav']),
    queryFn: async () => {
      const res = await fetchWithSchema('/api/static-pages/nav');
      if (!res.ok) throw new Error('Failed to fetch custom static pages');
      return res.json();
    },
    staleTime: 2 * 60 * 1000 // 2 minutes - reduced for faster updates in production
  });
  
  // Check if all data is loaded to prevent layout shift (must be after useQuery declarations)
  const isDataLoaded = !isLoadingSettings && !isLoadingAppConfig && !isLoadingMain && !isLoadingCustom;

  const { isActive: siteVideoBackdrop, isHome } = useCustomSiteBackdrop();
  const useTransparentHeroNav = siteVideoBackdrop && isHome;

  const [heroNavSolid, setHeroNavSolid] = useState(false);

  useEffect(() => {
    if (!useTransparentHeroNav) {
      setHeroNavSolid(false);
      return;
    }
    const onScroll = () => setHeroNavSolid(window.scrollY > 56);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [useTransparentHeroNav]);

  const transparentOverlayNav = useTransparentHeroNav && !heroNavSolid;
  /** Site custom cu video backdrop: navbar Apple dark glass */
  const appleDarkNav = siteVideoBackdrop;
  /** Text alb pe fundal întunecat / video */
  const lightForegroundNav = appleDarkNav;

  const appleGhostBtn =
    "rounded-full border border-white/10 bg-white/10 text-white/90 shadow-sm backdrop-blur-md hover:bg-white/18 hover:text-white active:bg-white/22 focus-visible:ring-white/35";

  const applePrimaryBtn =
    "rounded-full border-0 bg-white/95 text-gray-900 shadow-md hover:bg-white font-medium";

  const appleDropdownContent =
    "border-white/12 bg-[rgba(28,28,30,0.92)] text-white backdrop-blur-2xl";

  const appleDropdownItem =
    "text-white/90 focus:bg-white/12 focus:text-white";

  useEffect(() => {
    if (!hasAnimated && isDataLoaded) {
      // Apply animations on first load only after all data is loaded
      // First make navbar visible, then apply animations
      const timer = setTimeout(() => {
        setHasAnimated(true);
        sessionStorage.setItem('navbar-animated', 'true');
      }, 100);
      
      return () => clearTimeout(timer);
    } else if (isDataLoaded && hasAnimated) {
      // If already animated but data just loaded, ensure navbar is visible
      // This handles the case where hasAnimated was true from sessionStorage
    }
  }, [hasAnimated, isDataLoaded]);

  // Debug query to see all pages - only in development
  const { data: debugPages } = useQuery<StaticPage[]>({
    queryKey: getSchemaScopedQueryKey(['static-pages-debug']),
    queryFn: async () => {
      const res = await fetchWithSchema('/api/static-pages/debug');
      if (!res.ok) throw new Error('Failed to fetch debug pages');
      return res.json();
    },
    staleTime: 2 * 60 * 1000, // 2 minutes - reduced for faster updates in production
    enabled: import.meta.env.DEV, // Only fetch in development mode
  });

  // Construiește meniul principal dinamic
  // Sortare după sort_order (dacă există), altfel păstrăm ordinea din API
  const sortedMainPages = (mainPages || []).sort((a, b) => {
    const aOrder = a.sort_order ?? 9999;
    const bOrder = b.sort_order ?? 9999;
    return aOrder - bOrder;
  });
  
  const mainNavLinks = sortedMainPages.map(p => ({ href: `/${p.slug}`, label: p.menu_label }));

  // Sortare custom pages după sort_order
  const sortedCustomPages = (customPages || []).sort((a, b) => {
    const aOrder = a.sort_order ?? 9999;
    const bOrder = b.sort_order ?? 9999;
    return aOrder - bOrder;
  });

  // Linkuri suplimentare custom sub "Mai multe" (merg pe /page/{slug} sau /site/{slug})
  const customNavLinks = sortedCustomPages.map(p => ({
    href: p.is_hub_link ? `/site/${p.slug}` : `/page/${p.slug}`,
    label: p.menu_label
  }));

  // Toate linkurile (fără Contact hardcodat)
  // Filter out /services link if booking is not enabled AND not in presentation mode
  // In presentation mode, services should be visible even without booking
  const allLinks = useMemo(() => {
    // Don't filter if appConfig is not loaded yet
    if (!appConfig) {
      return mainNavLinks;
    }
    
    // Services link should be visible in all modes
    return mainNavLinks.filter(link => {
      // Only filter out /services if booking is disabled AND not in presentation mode
      if (!isBookingEnabled && !isWebsitePresentation && (link.href === '/services' || link.href.startsWith('/services'))) {
        return false;
      }
      return true;
    });
  }, [mainNavLinks, isBookingEnabled, appConfig, isWebsitePresentation]);

  // Primele 6 linkuri în navbar, restul sub "Mai multe" (inclusiv custom)
  const visibleLinks = useMemo(() => allLinks.slice(0, 6), [allLinks]);
  const hiddenLinks = useMemo(() => [
    ...allLinks.slice(6),
    ...customNavLinks
  ], [allLinks, customNavLinks]);



  // Close menu on location change
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location]);

  const getDashboardLink = () => {
    if (!userData || !userData.role) {
      return "/dashboard"; // Fallback to redirect component if role not loaded
    }
    // Return the correct dashboard route based on user role
    const role = (userData.role as string).toLowerCase().trim();
    if (role === 'admin' || role === UserRole.ADMIN.toLowerCase()) {
      return '/dashboard/admin';
    } else if (role === 'employee' || role === UserRole.EMPLOYEE.toLowerCase()) {
      return '/dashboard/employee';
    } else if (role === 'patient' || role === UserRole.PATIENT.toLowerCase()) {
      return '/dashboard/patient';
    }
    // Default fallback
    return "/dashboard";
  };

  const handleDashboardClick = () => {
    // Use navigate programmatically to ensure proper routing
    if (userData && userData.role) {
      const role = (userData.role as string).toLowerCase().trim();
      if (role === 'admin' || role === UserRole.ADMIN.toLowerCase()) {
        navigate('/dashboard/admin', { replace: true });
      } else if (role === 'employee' || role === UserRole.EMPLOYEE.toLowerCase()) {
        navigate('/dashboard/employee', { replace: true });
      } else if (role === 'patient' || role === UserRole.PATIENT.toLowerCase()) {
        navigate('/dashboard/patient', { replace: true });
      } else {
        // Fallback to redirect component
        navigate('/dashboard', { replace: true });
      }
    } else if (user) {
      // We have a user session but userData not loaded yet - use redirect component
      navigate('/dashboard', { replace: true });
    } else {
      // No user - redirect to login
      navigate('/login', { replace: true });
    }
  };

  const handleLogout = async () => {
    await logout();
  };

  return (
    <header
      id="site-navbar"
      data-no-theme-colors
      className={cn(
        "w-full sticky top-0 z-[100] overflow-visible transition-[background-color,backdrop-filter] duration-300",
        appleDarkNav
          ? transparentOverlayNav
            ? appleNavShellHeroClass
            : appleNavShellSolidClass
          : transparentOverlayNav
            ? "border-transparent bg-transparent backdrop-blur-none supports-[backdrop-filter]:bg-transparent"
            : "border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60"
      )}
      style={{ minHeight: "64px" }}
    >
      <div 
        className="w-full container mx-auto px-4 sm:px-6 lg:px-8 flex h-16 md:h-20 items-center overflow-visible relative"
        style={{ minHeight: '64px', height: 'auto' }}
      >
        {/* Logo */}
        <div 
          className={cn(
            "flex items-center justify-center relative z-[1002] flex-shrink-0",
            !hasAnimated && "animate-fade-in opacity-0"
          )}
          style={!hasAnimated ? { 
            minWidth: '156px',
            animationDelay: '0ms',
            animationFillMode: 'forwards'
          } : { minWidth: '156px' }}
        >
          <Link
            href="/"
            className={cn(
              "flex items-center justify-center space-x-2",
              lightForegroundNav && "drop-shadow-[0_2px_8px_rgba(0,0,0,0.65)]"
            )}
          >
            {isLoadingSettings ? (
              <div 
                className="bg-gray-200 animate-pulse rounded-md flex-shrink-0" 
                style={{ 
                  aspectRatio: '235/85',
                  height: '64px',
                  width: '180px',
                  minHeight: '64px',
                  maxHeight: '98px',
                }}
              />
            ) : settings?.logo_svg_code && settings.logo_svg_code.trim() !== '' ? (
              // Prioritize SVG code if present and not empty
              <div 
                className="flex items-center justify-center relative overflow-hidden flex-shrink-0"
                style={{ 
                  aspectRatio: '235/85',
                  height: '64px',
                  width: '180px',
                  minHeight: '64px',
                  minWidth: '180px',
                  maxHeight: '98px',
                  maxWidth: '270px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginTop: '-6px',
                }}
                dangerouslySetInnerHTML={{ __html: settings.logo_svg_code }}
              />
            ) : settings?.logo_url ? (
              // Fallback la logo-ul dinamic din setări
              <div 
                className="flex items-center justify-center relative overflow-hidden flex-shrink-0"
                style={{ 
                  aspectRatio: '235/85',
                  height: '64px',
                  width: '180px',
                  minHeight: '64px',
                  minWidth: '180px',
                  maxHeight: '98px',
                  maxWidth: '270px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginTop: '-6px',
                }}
              >
                <img
                  src={settings.logo_url}
                  alt={`${settings.app_name || ''} Logo`}
                  className="navbar-logo-image"
                  style={{ 
                    width: '100%',
                    height: '100%',
                    display: 'block',
                    objectFit: 'contain',
                    objectPosition: 'center',
                    aspectRatio: '235/85',
                  }}
                  width={270}
                  height={98}
                  loading="eager"
                  fetchpriority="high"
                />
              </div>
            ) : (
              // Fallback la text dacă nu există niciun logo
              <span 
                className={cn(
                  "text-lg md:text-xl font-bold flex-shrink-0",
                  lightForegroundNav ? "text-white" : textColorClass
                )}
                style={{
                  minWidth: '133px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  lineHeight: '1.2',
                }}
              >
                {settings?.app_name || ''}
              </span>
            )}
          </Link>
        </div>

        {/* Desktop Navigation - Centrat (o singură linie, fără wrap) */}
        <nav className="hidden lg:flex flex-nowrap items-center gap-x-4 xl:gap-x-6 text-sm font-medium absolute left-1/2 transform -translate-x-1/2 shrink-0 whitespace-nowrap">
          {isDataLoaded && visibleLinks.map((link, index) => (
            <div
              key={link.href}
              className={cn(!hasAnimated && "animate-fade-in-up opacity-0")}
              style={!hasAnimated ? {
                animationDelay: `${50 + index * 50}ms`,
                animationFillMode: 'forwards'
              } : {}}
            >
              <NavLink href={link.href} heroOverlay={lightForegroundNav}>
                {link.label}
              </NavLink>
            </div>
          ))}

          {isDataLoaded && hiddenLinks.length > 0 && (
            <div
              className={cn(!hasAnimated && "animate-fade-in-up opacity-0")}
              style={!hasAnimated ? {
                animationDelay: `${50 + visibleLinks.length * 50}ms`,
                animationFillMode: 'forwards'
              } : {}}
            >
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    className={cn(
                      "flex items-center gap-1 text-sm font-medium",
                      lightForegroundNav && appleGhostBtn
                    )}
                  >
                    {t('public.navbar.more', 'Mai multe')}
                    <ChevronDown className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  className={cn(appleDarkNav && appleDropdownContent)}
                >
                  {hiddenLinks.map(link => (
                    <DropdownMenuItem
                      key={link.href}
                      asChild
                      className={cn(appleDarkNav && appleDropdownItem)}
                    >
                      <Link href={link.href}>{link.label}</Link>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}
        </nav>
        
        <div className="flex-grow" />

        {/* Desktop User Actions */}
        <div className="hidden lg:flex items-center gap-2 xl:gap-3 relative z-[1001]">
          {contactPhone && (
            <div
              className={cn("flex items-center gap-2", !hasAnimated && "animate-fade-in-up opacity-0")}
              style={!hasAnimated ? {
                animationDelay: `${80 + visibleLinks.length * 50}ms`,
                animationFillMode: 'forwards'
              } : {}}
            >
              <a
                href={phoneToTelHref(contactPhone)}
                className={cn(
                  lightForegroundNav
                    ? appleNavContactPillClass()
                    : "inline-flex items-center gap-1.5 rounded-full border border-border bg-muted/60 px-3 py-1.5 text-sm font-medium text-foreground transition-colors hover:bg-muted [touch-action:manipulation]"
                )}
                aria-label={`Sună la ${contactPhone}`}
              >
                <Phone className="h-4 w-4 shrink-0 opacity-90" />
                <span>{contactPhone}</span>
              </a>
              <a
                href={phoneToWhatsAppHref(
                  contactPhone,
                  isWebReadyTenant() ? { prefillMessage: WEBREADY_WHATSAPP_GDPR_MESSAGE } : undefined
                )}
                target="_blank"
                rel="noopener noreferrer"
                className={cn(
                  lightForegroundNav
                    ? appleNavContactPillClass()
                    : "inline-flex items-center gap-1.5 rounded-full border border-border bg-muted/60 px-3 py-1.5 text-sm font-medium text-foreground transition-colors hover:bg-muted [touch-action:manipulation]"
                )}
                aria-label="Trimite mesaj pe WhatsApp"
              >
                <WhatsAppIcon
                  className={cn('h-4 w-4 shrink-0', lightForegroundNav ? 'text-[#5FE89A]' : 'text-[#128C7E]')}
                />
                <span>WhatsApp</span>
              </a>
            </div>
          )}
          {user ? (
            <div
              className={cn(!hasAnimated && "animate-fade-in-up opacity-0")}
              style={!hasAnimated ? {
                animationDelay: `${100 + visibleLinks.length * 50}ms`,
                animationFillMode: 'forwards'
              } : {}}
            >
              {/* User Profile Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    className={cn(
                      "flex items-center gap-2",
                      lightForegroundNav && appleGhostBtn
                    )}
                  >
                    <User className="h-5 w-5" />
                    <span className="hidden md:inline">{userData?.firstName}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  className={cn(appleDarkNav && appleDropdownContent)}
                >
                  <DropdownMenuItem
                    onClick={handleDashboardClick}
                    className={cn(appleDarkNav && appleDropdownItem)}
                  >
                    Dashboard
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className={cn(appleDarkNav && "bg-white/12")} />
                  <DropdownMenuItem
                    onClick={handleLogout}
                    className={cn(
                      appleDarkNav ? "text-red-400 focus:bg-red-500/15 focus:text-red-300" : "text-red-500"
                    )}
                  >
                    <LogOut className="mr-2 h-4 w-4" />
                    <span>Deconectare</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          ) : (
            isBookingEnabled && (
              <div 
                className={cn("flex items-center space-x-2", !hasAnimated && "animate-fade-in-up opacity-0")}
                style={!hasAnimated ? {
                  animationDelay: `${100 + visibleLinks.length * 50}ms`,
                  animationFillMode: 'forwards'
                } : {}}
              >
                <Button
                  asChild
                  className={lightForegroundNav ? applePrimaryBtn : undefined}
                >
                  <Link href={bookingButtonHref}>
                    {React.createElement(bookingButtonIcon, { className: "mr-2 h-4 w-4" })}
                    {bookingButtonText}
                  </Link>
                </Button>
              </div>
            )
          )}
        </div>

        {/* Mobile Menu — dropdown lipit de marginea dreaptă */}
        <div
          className={cn(
            "lg:hidden ml-auto shrink-0 -mr-4 sm:-mr-6",
            !hasAnimated && "animate-fade-in-up opacity-0"
          )}
          style={
            !hasAnimated
              ? { animationDelay: '100ms', animationFillMode: 'forwards' }
              : undefined
          }
        >
          <DropdownMenu open={isMenuOpen} onOpenChange={setIsMenuOpen}>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className={cn("h-10 w-10", lightForegroundNav && appleGhostBtn)}
                aria-label={isMenuOpen ? 'Închide meniul' : 'Deschide meniul'}
              >
                <Menu className="h-5 w-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              side="bottom"
              sideOffset={8}
              collisionPadding={{ top: 12, right: 0, bottom: 12, left: 12 }}
              className={cn(
                'z-[9999] w-[min(300px,calc(100vw-1rem))] max-h-[min(70vh,520px)] overflow-y-auto rounded-2xl rounded-tr-none p-2 shadow-2xl',
                appleDarkNav
                  ? 'custom-liquid-glass mobile-nav-dropdown border border-white/14 text-white'
                  : 'border-border bg-background'
              )}
            >
              {allLinks.map((link) => (
                <DropdownMenuItem
                  key={link.href}
                  asChild
                  className={cn(
                    'min-h-[44px] cursor-pointer rounded-xl px-3 py-2.5 text-[15px]',
                    appleDarkNav && appleDropdownItem
                  )}
                >
                  <Link href={link.href} onClick={() => setIsMenuOpen(false)}>
                    {link.label}
                  </Link>
                </DropdownMenuItem>
              ))}

              {user ? (
                <>
                  <DropdownMenuSeparator className={cn(appleDarkNav && 'bg-white/10')} />
                  <DropdownMenuItem
                    disabled
                    className={cn(
                      'opacity-100',
                      appleDarkNav ? 'text-white/55 focus:bg-transparent' : 'text-muted-foreground'
                    )}
                  >
                    Bun venit, {userData?.firstName}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => {
                      handleDashboardClick();
                      setIsMenuOpen(false);
                    }}
                    className={cn(
                      'min-h-[44px] cursor-pointer rounded-xl px-3 py-2.5',
                      appleDarkNav && appleDropdownItem
                    )}
                  >
                    <User className="h-4 w-4" />
                    Dashboard
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => {
                      handleLogout();
                      setIsMenuOpen(false);
                    }}
                    className={cn(
                      'min-h-[44px] cursor-pointer rounded-xl px-3 py-2.5',
                      appleDarkNav
                        ? 'text-red-400 focus:bg-red-500/15 focus:text-red-300'
                        : 'text-red-500 focus:text-red-700'
                    )}
                  >
                    <LogOut className="h-4 w-4" />
                    Deconectare
                  </DropdownMenuItem>
                </>
              ) : (
                isBookingEnabled && (
                  <>
                    <DropdownMenuSeparator className={cn(appleDarkNav && 'bg-white/10')} />
                    <DropdownMenuItem asChild className={cn('p-1', appleDarkNav && 'focus:bg-transparent')}>
                      <Button className={cn('w-full', appleDarkNav && applePrimaryBtn)} asChild>
                        <Link href={bookingButtonHref} onClick={() => setIsMenuOpen(false)}>
                          {React.createElement(bookingButtonIcon, { className: 'mr-2 h-4 w-4' })}
                          {bookingButtonText}
                        </Link>
                      </Button>
                    </DropdownMenuItem>
                  </>
                )
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

    </header>
  );
}
